import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';

import useTotalsInitialValues from './hooks/useTotalsInitialValues';
import useReponsive from 'Common/hooks/useResponsive';
import getTotals from './api/getTotals';

import ImpactMobile from 'Impact/layouts/ImpactMobile';
import ImpactSmallScreen from 'Impact/layouts/ImpactSmallScreen';
import ImpactBigScreen from 'Impact/layouts/ImpactBigScreen';
import ImpactLargeScreen from 'Impact/layouts/ImpactLargeScreen';

import HeaderContainer from 'Common/components/PageHeader/HeaderContainer';
import HeaderTitle from 'Common/components/PageHeader/HeaderTitle';

import getStatistics from 'Impact/api/getStatistics';
import useStatisticsInitialValues from 'Impact/hooks/useStatisticsInitialValues';
import loadImpactStepNames from 'Impact/utils/loadImpactStepNames';

import { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import colors from 'Application/theme/colors';

import styled from 'styled-components/macro';
import showToastError from 'Common/utils/showToastError';
import LoaderWrapper from 'Common/components/LoaderWrapper';
import Loader from 'Common/components/Loader';
import getProjectDefaults from 'Preferences/api/getProjectDefaults';
import useAbortController from 'Common/hooks/useAbortController';
import ReactTimeAgo from 'react-time-ago';
import sizes from 'Application/theme/sizes';
import Icon from 'Common/components/icons/Icon';

const Wrapper = styled.div`
	padding: 20px;
	width: 100%;
	transition: width 0.5s, height 0.5s;

	${({ $isMobile }) =>
		$isMobile &&
		css`
			width: 100vw;
		`}
`;

export const AlertWrapper = styled.div`
	display: flex;
	align-items: center;
	background-color: #fff7e5;
	border-radius: 4px;
	padding: 16px;
	border-left: 4px solid #ffb82e;

	svg {
		color: #ffb82e;
		font-size: 20px;
		margin-right: 12px;
	}

	h2 {
		margin: 0;
		font-weight: 400;
		font-size: 14px;
		color: rgba(0, 0, 0, 0.87);
	}
`;

export const BoldText = styled.span`
	font-weight: 700;
`;

export const StyledLink = styled.a`
	color: ${colors.primary.main};
	text-decoration: underline;
	cursor: pointer;

	&:hover {
		opacity: 0.8;
	}
`;

const LastUpdatedWrapper = styled.div`
	background-color: ${colors.common.headerGrey};
	color: ${colors.text.primary};
	font-size: 14px;
	margin: 0;
	padding-top: ${sizes.spacing(1.5)};
	padding-bottom: ${sizes.spacing(1.5)};
	padding-left: ${sizes.spacing(2)};
`;

const Impact = () => {
	const { t } = useTranslation();
	const { isMobile, isTablet, isSmallscreen, isMediumScreen, isBigScreen, isFullHd, is4k } = useReponsive();

	const statisticsInitialValues = useStatisticsInitialValues();
	const totalsInitialValues = useTotalsInitialValues();

	const abortController = useAbortController();

	const [statistics, setStatistics] = useState(statisticsInitialValues);
	const [totals, setTotals] = useState(totalsInitialValues);
	const [stepTemplates, setStepTemplates] = useState();

	const [isLoading, setIsLoading] = useState(true);

	const currentUser = useSelector(state => state.user);

	useEffect(() => {
		(async () => {
			try {
				setIsLoading(true);
				const response = await getStatistics(abortController.signal);
				const stepNames = await loadImpactStepNames(abortController.signal);
				const { data: totalsData } = await getTotals(abortController.signal);
				const {
					data: { allowedPercentageOfRestrictedIndustries },
				} = await getProjectDefaults(abortController.signal);

				setTotals({ ...totalsData, allowedPercentageOfRestrictedIndustries });
				setStepTemplates(stepNames);
				setStatistics(response.data);
				setIsLoading(false);
			} catch (e) {
				showToastError(e);
			}
		})();
	}, [abortController.signal]);

	const crowdPieData = {
		labels: ['active', 'not active'],
		datasets: [totals.totalCrowdInvestors, totals.notActiveCrowdInvestors],
		colors: [colors.primary.main, colors.primary.dark],
	};

	const pieData = {
		labels: ['male', 'female'],
		datasets: [totals.totalMaleCrowdInvestors, totals.totalFemaleCrowdInvestors],
		colors: [colors.primary.main, colors.primary.dark],
	};

	return (
		<>
			<HeaderContainer>
				<HeaderTitle>{`${t('Welcome')}, ${currentUser.firstName}!`}</HeaderTitle>
			</HeaderContainer>
			<AlertWrapper>
				<Icon icon="warning" size="small" />
				<h2>
					{t('As of today, ')}
					<BoldText>Pulse will be in frozen state. </BoldText>
					{t("This means that you won't be able to enter or edit any information until Pulse v2 is released. ")}
					<StyledLink
						href="https://www.notion.so/ecoligo/Pulse-migration-All-you-need-to-know-2572e2e27e4080678bf7f28ade021b6c"
						target="_blank"
						rel="noopener noreferrer"
					>
						{t('Read more ')}
					</StyledLink>
					{t('about this.')}
				</h2>
			</AlertWrapper>
			{totals.createdDate && (
				<LastUpdatedWrapper>
					Data last calculated:{' '}
					<ReactTimeAgo date={new Date(totals.createdDate)} timeStyle="round" tooltip={false} />
				</LastUpdatedWrapper>
			)}
			{isLoading ? (
				<LoaderWrapper $isFullPage>
					<Loader />
				</LoaderWrapper>
			) : (
				<Wrapper $isMobile={isMobile}>
					<Grid container spacing={2}>
						{is4k ? (
							<ImpactLargeScreen
								statistics={statistics}
								crowdPieData={crowdPieData}
								pieData={pieData}
								totals={totals}
								filterValues={stepTemplates}
							/>
						) : isBigScreen || isFullHd ? (
							<ImpactBigScreen
								statistics={statistics}
								crowdPieData={crowdPieData}
								pieData={pieData}
								totals={totals}
								filterValues={stepTemplates}
							/>
						) : isSmallscreen || isMediumScreen ? (
							<ImpactSmallScreen
								statistics={statistics}
								crowdPieData={crowdPieData}
								pieData={pieData}
								totals={totals}
								filterValues={stepTemplates}
							/>
						) : (
							(isTablet || isMobile) && (
								<ImpactMobile
									statistics={statistics}
									crowdPieData={crowdPieData}
									pieData={pieData}
									totals={totals}
									filterValues={stepTemplates}
								/>
							)
						)}
					</Grid>
				</Wrapper>
			)}
		</>
	);
};
export default Impact;
